/// <amd-module name="VSS/ExtServices/ExtensionDataService" />
import { BearerTokenProvider } from "VSS/Platform/Authentication";
import { IVssPageContext, ServiceOptions, Services, setRemoteSerializationSettings, VssService } from "VSS/Platform/Context";
import { equals } from "VSS/Core/Util/String";
import { ExtensionDataCollection } from "./Contracts/ExtensionManagement";
import { getExtensionManagementClient, IExtensionManagementRestClient } from "./RestClient/ExtensionManagement";
import { getExtensionIdentifier } from "VSS/Ext/ExternalContentUtil";

export const ExtensionDataServiceName = "ms.vss-features.extension-data-service";

/**
 * Interface for options that can be supplied with document actions
 */
export interface IDocumentOptions {
    /**
     * The scope of where the document is stored. Can be Default or User.
     */
    scopeType?: string;

    /**
     * The value of the scope where the document is stored. Can be Current or Me.
     */
    scopeValue?: string;

    /**
     * The default value to return when using getValue(). If the document has no value,
     * this value will be used instead.
     */
    defaultValue?: any;
}

export interface IExtensionDataManager {
    /**
     * Returns a promise for retrieving a setting at the provided key and scope
     *
     * @param key The key to retrieve a value for
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    getValue<T>(key: string, documentOptions: IDocumentOptions): Promise<T>;

    /**
     * Returns a promise for saving a setting at the provided key and scope
     *
     * @param key The key to save a value for
     * @param value The value to save
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    setValue<T>(key: string, value: T, documentOptions: IDocumentOptions): Promise<T>;

    /**
     * Returns a promise for getting a document with the provided id in the provided collection
     *
     * @param collectionName The name of the collection where the document lives
     * @param id The id of the document in the collection
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    getDocument(collectionName: string, id: string, documentOptions: IDocumentOptions): Promise<any>;

    /**
     * Returns a promise for getting all of the documents in the provided collection
     *
     * @param collectionName The name of the collection where the document lives
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    getDocuments(collectionName: string, documentOptions?: IDocumentOptions): Promise<any[]>;

    /**
     * Returns a promise for creating a document in the provided collection
     *
     * @param collectionName The name of the collection where the document lives
     * @param doc The document to store
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    createDocument(collectionName: string, doc: any, documentOptions: IDocumentOptions): Promise<any>;

    /**
     * Returns a promise for setting a document in the provided collection
     * Creates the document if it does not exist, otherwise updates the existing document with the id provided
     *
     * @param collectionName The name of the collection where the document lives
     * @param doc The document to store
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    setDocument(collectionName: string, doc: any, documentOptions: IDocumentOptions): Promise<any>;

    /**
     * Returns a promise for updating a document in the provided collection
     * A document with the id provided must exist
     *
     * @param collectionName The name of the collection where the document lives
     * @param doc The document to store
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    updateDocument(collectionName: string, doc: any, documentOptions: IDocumentOptions): Promise<any>;

    /**
     * Returns a promise for deleting the document at the provided scope, collection and id
     *
     * @param collectionName The name of the collection where the document lives
     * @param id The id of the document in the collection
     * @param documentOptions Extension document options, the default scope value is account-wide
     */
    deleteDocument(collectionName: string, id: string, documentOptions: IDocumentOptions): Promise<void>;

    /**
     * Returns a promise for querying a set of collections
     *
     * @param collections The list of collections to query. Assumes Default Scope Type and Current Scope Value
     */
    queryCollectionsByName(collectionNames: string[]): Promise<ExtensionDataCollection[]>;

    /**
     * Returns a promise for querying a set of collections
     *
     * @param collections The list of collections to query. Each collection will contain its collectionName, scopeType, and scopeValue
     */
    queryCollections(collections: ExtensionDataCollection[]): Promise<ExtensionDataCollection[]>;
}

export interface IExtensionDataService {
    /**
     * Gets a class that can be used to manage extension data.
     *
     * @param extensionId Id of the extension (publisher.extension)
     * @param accessToken Access token to use for the extension
     */
    getExtensionDataManager(extensionId: string, accessToken: string): IExtensionDataManager;
}

class ExtensionDataService extends VssService implements IExtensionDataService {
    public getExtensionDataManager(extensionId: string, accessToken: string): IExtensionDataManager {
        return new ExtensionDataManager(this.pageContext, extensionId, accessToken);
    }
}

const DEFAULT_SCOPE_TYPE = "Default";
const CURRENT_DEFAULT_SCOPE_VALUE = "Current";
const USER_SCOPE_TYPE = "User";
const CURRENT_USER_SCOPE_VALUE = "Me";
const SETTINGS_COLLECTION_NAME = "$settings";

class ExtensionDataManager implements IExtensionDataManager {
    private _pageContext: IVssPageContext;
    private _publisherName: string;
    private _extensionName: string;
    private _extensionClient: IExtensionManagementRestClient;

    constructor(pageContext: IVssPageContext, extensionId: string, accessToken: string) {
        this._pageContext = pageContext;

        const id = getExtensionIdentifier(extensionId);
        this._publisherName = id.publisherId;
        this._extensionName = id.extensionId;

        this._extensionClient = getExtensionManagementClient(this._pageContext, {
            authTokenProvider: new BearerTokenProvider(accessToken)
        });

        setRemoteSerializationSettings(this, { proxyFunctions: true });
    }

    public getValue<T>(key: string, documentOptions?: IDocumentOptions): Promise<T> {
        const options = this._checkDocumentOptions(documentOptions);
        return this._extensionClient
            .getDocumentByName(this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, SETTINGS_COLLECTION_NAME, key)
            .then(
                (doc: any) => {
                    return this.prepareResult(doc.value);
                },
                (error: Error) => {
                    return options.defaultValue;
                }
            );
    }

    public getValues(keys: string[], documentOptions?: IDocumentOptions): Promise<{ [key: string]: any }> {
        const options = this._checkDocumentOptions(documentOptions);

        return this._extensionClient
            .getDocumentsByName(this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, SETTINGS_COLLECTION_NAME)
            .then(
                (docs: any[]) => {
                    const values: { [key: string]: any } = {};

                    const docsById: { [key: string]: any } = {};
                    for (let i = 0; i < docs.length; i++) {
                        const doc = docs[i];
                        if (doc.id) {
                            docsById[doc.id] = doc;
                        }
                    }

                    keys.forEach((key: string) => {
                        if (key in docsById && docsById[key].hasOwnProperty("value")) {
                            values[key] = docsById[key].value;
                        } else {
                            values[key] = options.defaultValue;
                        }
                    });
                    return this.prepareResult(values);
                },
                (error: Error) => {
                    return {};
                }
            );
    }

    public setValue<T>(key: string, value: T, documentOptions?: IDocumentOptions): Promise<T> {
        const options = this._checkDocumentOptions(documentOptions);
        const doc = {
            id: key,
            value,
            __etag: -1
        };

        return this._extensionClient
            .setDocumentByName(doc, this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, SETTINGS_COLLECTION_NAME)
            .then((doc: any) => {
                return this.prepareResult(doc.value);
            });
    }

    public setValues(keyValuePairs: { [key: string]: any }, documentOptions?: IDocumentOptions): Promise<any[]> {
        const options = this._checkDocumentOptions(documentOptions);

        return Promise.all(
            Object.keys(keyValuePairs).map(key => {
                const doc = {
                    id: key,
                    value: keyValuePairs[key],
                    __etag: -1
                };

                return this._extensionClient
                    .setDocumentByName(
                        doc,
                        this._publisherName,
                        this._extensionName,
                        options.scopeType!,
                        options.scopeValue!,
                        SETTINGS_COLLECTION_NAME
                    )
                    .then((doc: any) => {
                        return this.prepareResult(doc.value);
                    });
            })
        );
    }

    public getDocument(collectionName: string, id: string, documentOptions?: IDocumentOptions): Promise<any> {
        const options = this._checkDocumentOptions(documentOptions);
        return this._extensionClient
            .getDocumentByName(this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, collectionName, id)
            .then(doc => this.prepareResult(doc));
    }

    public getDocuments(collectionName: string, documentOptions?: IDocumentOptions): Promise<any[]> {
        const options = this._checkDocumentOptions(documentOptions);
        return this._extensionClient
            .getDocumentsByName(this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, collectionName)
            .then(docs => this.prepareResult(docs));
    }

    public createDocument(collectionName: string, doc: any, documentOptions?: IDocumentOptions): Promise<any> {
        const options = this._checkDocumentOptions(documentOptions);
        return this._extensionClient
            .createDocumentByName(doc, this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, collectionName)
            .then(doc => this.prepareResult(doc));
    }

    public setDocument(collectionName: string, doc: any, documentOptions?: IDocumentOptions): Promise<any> {
        const options = this._checkDocumentOptions(documentOptions);
        return this._extensionClient
            .setDocumentByName(doc, this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, collectionName)
            .then(doc => this.prepareResult(doc));
    }

    public updateDocument(collectionName: string, doc: any, documentOptions?: IDocumentOptions): Promise<any> {
        const options = this._checkDocumentOptions(documentOptions);
        return this._extensionClient
            .updateDocumentByName(doc, this._publisherName, this._extensionName, options.scopeType!, options.scopeValue!, collectionName)
            .then(doc => this.prepareResult(doc));
    }

    public deleteDocument(collectionName: string, id: string, documentOptions?: IDocumentOptions): Promise<void> {
        const options = this._checkDocumentOptions(documentOptions);
        return this._extensionClient.deleteDocumentByName(
            this._publisherName,
            this._extensionName,
            options.scopeType!,
            options.scopeValue!,
            collectionName,
            id
        );
    }

    public queryCollectionsByName(collectionNames: string[]): Promise<ExtensionDataCollection[]> {
        var collections = collectionNames.map((collectionName: string) => {
            return {
                collectionName: collectionName,
                scopeType: DEFAULT_SCOPE_TYPE,
                scopeValue: CURRENT_DEFAULT_SCOPE_VALUE,
                documents: []
            };
        });
        const query = { collections: collections };
        return this._extensionClient.queryCollectionsByName(query, this._publisherName, this._extensionName).then(docs => this.prepareResult(docs));
    }

    /**
     * Returns a promise for querying a set of collections
     *
     * @param collections The list of collections to query. Each collection will contain its collectionName, scopeType, and scopeValue
     */
    public queryCollections(collections: ExtensionDataCollection[]): Promise<ExtensionDataCollection[]> {
        const query = { collections: collections };
        return this._extensionClient.queryCollectionsByName(query, this._publisherName, this._extensionName).then(docs => this.prepareResult(docs));
    }

    private prepareResult(result: any): any {
        if (typeof result === "object") {
            setRemoteSerializationSettings(result, { serializeUnderscoreProperties: true });
        }
        return result;
    }

    private _checkDocumentOptions(documentOptions: IDocumentOptions | undefined): IDocumentOptions {
        if (!documentOptions) {
            documentOptions = {};
        }

        if (!documentOptions.scopeType) {
            documentOptions.scopeType = DEFAULT_SCOPE_TYPE;
        }

        if (!documentOptions.scopeValue) {
            documentOptions.scopeValue = equals(documentOptions.scopeType, USER_SCOPE_TYPE, true)
                ? CURRENT_USER_SCOPE_VALUE
                : CURRENT_DEFAULT_SCOPE_VALUE;
        }

        return documentOptions;
    }
}

Services.add(ExtensionDataServiceName, { serviceFactory: ExtensionDataService, options: ServiceOptions.Remotable });
